import FloatingPage from "components/General/FloatingPage"
import { useFormik, FormikProvider } from "formik"
import * as Yup from "yup"
import { Container} from "react-bootstrap"
import { useContext, useEffect, useState } from "react"
import styles from "./Quiz.module.scss"
import { toast } from "react-toastify"
import ClassQuizForm from "./ClassQuizForm"
import StudentClassQuizForm from "./StudentClassQuizForm"
import StudentQuizForm from "./StudentQuizForm"
import ProgressBar from "react-bootstrap/ProgressBar"
import * as teacher from "../../shared/routes/teacher"
import { useNavigate } from "react-router"
import { AuthContext } from "AuthContext"
import * as authModel from "../../models/auth"
import { AssessmentViewOptionsType } from "shared/types/assessmentTypes"

type QuizActivePart = "createClass" | "createClassAccount" | "studentQuiz" | "noQuizAccess"
interface CreateClassProps {
    className: string
    academicYear: string | null
    term: string | null
    gradeLevel: string | null
    standardId: string | null
    requireCompletion: "Yes" | "No"
    studentAssessmentViewOption: AssessmentViewOptionsType
    oneQuestionMode: boolean
}

interface CreateClassAccountProps {
    username: string
    password: string
    confirmPassword: string
}

const OnboardingQuiz = () => {
    const navigate = useNavigate()
    const auth = useContext(AuthContext)
    const [quizActivePart, setQuizActivePart] = useState<QuizActivePart>(null)
    const [quizProgress, setQuizProgress] = useState(50)
    const [createdClassId, setCreatedClassId] = useState<string>(null)

    const classFormInitialValues : CreateClassProps = {
        className: "",
        term: "Spring",
        academicYear: new Date().getFullYear().toString(),
        gradeLevel: null,
        standardId: null,
        requireCompletion: "No",
        studentAssessmentViewOption: "Grade",
        oneQuestionMode: true
    }

    const studentClassFormInitialValues = {
        username: "",
        password: "",
        confirmPassword: ""
    }

    useEffect(() => {
        authModel.information().then((authRes) => {
            if (authRes.isLoggedIn) {
                setQuizActivePart(authRes.isStudent || !authRes.trackingEnabled ? "studentQuiz" : "createClass")
            } else {
                setQuizActivePart("noQuizAccess")
            }
        })
    }, [])

    const createClass = async (values: CreateClassProps) => {
        try {
            if (!values.academicYear) values.academicYear = null // if academic year is empty string, make it null so that it could be stored in db
            const newClass = await teacher.createClass(values)
            setCreatedClassId(newClass.id)
            toast.success("Your class is successfully created.")
            setQuizProgress(100)

            // if student class already exists, skip student class creation page
            const studentExists = await teacher.checkIfStudentClassExists()
            if (studentExists) {
                navigate("/dashboard/teacher/overview", { state: {id: newClass.id} })
            } else {
                setQuizActivePart("createClassAccount")
            }
        } catch {
            toast.error("There has been an error creating your class. Please try again.")
        }
    }

    const createStudentClassAccount = async (values: CreateClassAccountProps) => {
        try {
            const res = await teacher.createStudentClassAccount(values)
            toast.success(res)
            navigate("/dashboard/teacher/overview", { state: {id: createdClassId} })
        } catch (err) {
            toast.error(err.response.data)
        }
    }

    const classFormik = useFormik({
        initialValues: classFormInitialValues,
        onSubmit: createClass,
        enableReinitialize: true,
        validationSchema: Yup
            .object()
            .shape({
                className: Yup
                    .string()
                    .max(40)
                    .label("Class Name")
                    .required("Class name is required."),
                term: Yup
                    .string()
                    .label("Academic Semester")
                    .required("Academic semester is required."),
                academicYear: Yup
                    .string()
                    .max(15)
                    .nullable()
                    .label("Academic Year"),
                gradeLevel: Yup.string().label("Grade Level").nullable(),
                standardId: Yup.string().label("Standard").nullable(),
                requireCompletion: Yup
                    .string()
                    .label("Require Lesson/Assessment Completion")
                    .required("Require completion option is required."),
            })
    })

    const studentClassAccountFormik = useFormik({
        initialValues: studentClassFormInitialValues,
        onSubmit: createStudentClassAccount,
        enableReinitialize: true,
        validationSchema: Yup
            .object()
            .shape({
                username: Yup
                    .string()
                    .max(150)
                    .label("Username")
                    .required("Username is required."),
                password: Yup
                    .string()
                    .label("Password")
                    .required("Password is required.")
                    .min(5, "Password must be 5 characters long")
                    .max(150)
                    .oneOf([Yup.ref('confirmPassword'), null], 'The passwords entered do not match.'),
                confirmPassword: Yup
                    .string()
                    .max(150)
                    .label("Confirm Password")
                    .required("Confrim Password is required."),
            })
    })

  return (
    <FloatingPage className={styles.background}>
        {!auth.isLoggedIn && <h1>This quiz can be taken only once when you sign up.</h1>}
        {quizActivePart !== "studentQuiz" && quizActivePart !== "noQuizAccess" && <ProgressBar now={quizProgress} className={`${styles.progressBar} w-50 text-center m-auto mb-4`} />}
        <h2 className="mb-3 fw-bolder">
            { quizActivePart === "createClass" || quizActivePart === "studentQuiz" ?  "Let's get started." : (quizActivePart === "noQuizAccess" ? "" : "Almost Done...") }
        </h2>
        <Container className="d-flex align-items-center justify-content-center">
            <FormikProvider value={quizActivePart === "createClass" ? classFormik : studentClassAccountFormik}>
                {quizActivePart === "createClass" && 
                    <ClassQuizForm formik={classFormik} />}
                    
                {quizActivePart === "createClassAccount" &&
                    <StudentClassQuizForm formik={studentClassAccountFormik} />}
            </FormikProvider>
            {quizActivePart === "studentQuiz" && <StudentQuizForm />}
        </Container>
    </FloatingPage>
  )
}

export default OnboardingQuiz
